import { useEffect, useState } from "react";
import { Plus, Trash2, Library, BookOpen, DoorOpen, Upload, X, Pencil, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const KINDS = [
  { key: "teachers", label: "Teachers", icon: Library, bg: "#FDE68A", placeholder: "e.g. Mrs. Smith", detail: "Subject / department (optional)" },
  { key: "subjects", label: "Subjects / classes", icon: BookOpen, bg: "#A7E4D3", placeholder: "e.g. Maths", detail: "Note (optional)" },
  { key: "rooms", label: "Rooms", icon: DoorOpen, bg: "#7DD3FC", placeholder: "e.g. Lab A", detail: "Location note (optional)" },
];

export default function Directory() {
  const [kind, setKind] = useState("teachers");
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", detail: "", subject_ids: [], new_pin: "" });

  useEffect(() => {
    api.get("/school/directory/subjects").then((r) => setSubjects(r.data)).catch(() => {});
  }, []);

  const load = (k) => api.get(`/school/directory/${k}`).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));

  useEffect(() => { load(kind); }, [kind]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/school/directory/${kind}`, { name, detail, ...(kind === "teachers" ? { subject_ids: selectedSubjects } : {}) });
      setName(""); setDetail(""); setSelectedSubjects([]);
      toast.success("Added");
      load(kind);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/school/directory/${kind}/${id}`);
      toast.success("Removed (also cleared from pupil timetables)");
      load(kind);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const active = KINDS.find((k) => k.key === kind);

  const doImport = async () => {
    if (!importText.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/school/directory/${kind}/import`, importText, { headers: { "Content-Type": "text/plain" } });
      toast.success(`Imported ${data.added} — skipped ${data.skipped} duplicate(s)`);
      setImportOpen(false);
      setImportText("");
      load(kind);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (editForm.new_pin && !/^[0-9]{4,8}$/.test(editForm.new_pin)) {
      toast.error("PIN must be 4–8 digits");
      return;
    }
    try {
      await api.put(`/school/directory/${kind}/${editItem.id}`, {
        name: editForm.name, detail: editForm.detail, subject_ids: editForm.subject_ids,
        new_pin: editForm.new_pin || undefined,
      });
      toast.success(editForm.new_pin ? "Saved — teacher login PIN updated" : "Saved");
      setEditItem(null);
      load(kind);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleEditSubject = (sid) => {
    const on = editForm.subject_ids.includes(sid);
    setEditForm({ ...editForm, subject_ids: on ? editForm.subject_ids.filter((x) => x !== sid) : [...editForm.subject_ids, sid] });
  };

  return (
    <div data-testid="directory-page">
      <span className="nb-label">Directory</span>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Teachers, subjects & rooms</h1>
      <p className="text-zinc-500 mt-2 max-w-xl">Pupils build their timetables by picking from these lists. Keep them tidy and up to date.</p>

      <div className="mt-8 flex flex-wrap gap-3 items-center" role="tablist">
        {KINDS.map((k) => (
          <button key={k.key} role="tab" data-testid={`directory-tab-${k.key}`} onClick={() => setKind(k.key)}
            className={`nb-btn px-4 py-2 text-sm ${kind === k.key ? "" : "bg-white text-zinc-500"}`}
            style={kind === k.key ? { backgroundColor: k.bg } : {}}>
            <k.icon className="w-4 h-4" /> {k.label}
          </button>
        ))}
        <button data-testid="directory-import-btn" onClick={() => setImportOpen(true)} className="nb-btn bg-white px-4 py-2 text-sm ml-auto">
          <Upload className="w-4 h-4" /> Import CSV
        </button>
      </div>

      <div className="mt-6 nb-card p-6">
        <form onSubmit={add} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="nb-label" htmlFor="dir-name">Name</label>
            <input id="dir-name" className="nb-input mt-2" data-testid="directory-name-input" placeholder={active.placeholder}
              value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="nb-label" htmlFor="dir-detail">{active.detail}</label>
            <input id="dir-detail" className="nb-input mt-2" data-testid="directory-detail-input"
              value={detail} onChange={(e) => setDetail(e.target.value)} />
          </div>
          <button type="submit" disabled={busy} data-testid="directory-add-btn" className="nb-btn px-5 py-2.5" style={{ backgroundColor: active.bg }}>
            <Plus className="w-4 h-4" /> Add
          </button>
        </form>
        {kind === "teachers" && subjects.length > 0 && (
          <div className="mt-4">
            <span className="nb-label">Subjects they teach (pick any)</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {subjects.map((s) => {
                const on = selectedSubjects.includes(s.id);
                return (
                  <button key={s.id} type="button" data-testid={`teacher-subject-chip-${s.id}`}
                    onClick={() => setSelectedSubjects(on ? selectedSubjects.filter((x) => x !== s.id) : [...selectedSubjects, s.id])}
                    className={`px-3 py-1.5 rounded-xl border-2 border-zinc-900 text-xs font-bold transition-[transform,box-shadow,background-color] duration-150 ${on ? "bg-[#A7E4D3] shadow-[2px_2px_0px_0px_#18181B]" : "bg-white text-zinc-500"}`}>
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 nb-card overflow-hidden">
        <table className="w-full border-collapse" data-testid="directory-table">
          <thead>
            <tr className="bg-zinc-900 text-white text-left">
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Name</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Detail</th>
              <th className="p-3 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t-2 border-zinc-900" data-testid={`directory-row-${item.id}`}>
                <td className="p-3 font-bold">{item.name}</td>
                <td className="p-3 text-sm text-zinc-500">
                  {item.detail || "—"}
                  {kind === "teachers" && (
                    <span className="flex flex-wrap gap-1.5 mt-1.5">
                      {(item.subject_ids || []).map((sid) => (
                        <span key={sid} className="px-2 py-0.5 rounded-lg border-2 border-zinc-900 bg-[#A7E4D3] text-[11px] font-bold text-zinc-900">
                          {subjects.find((s) => s.id === sid)?.name || "?"}
                        </span>
                      ))}
                      {item.has_pin && <span className="px-2 py-0.5 rounded-lg border-2 border-zinc-900 bg-[#FDE68A] text-[11px] font-bold" data-testid={`teacher-pin-badge-${item.id}`}>login set</span>}
                    </span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <div className="flex gap-2 justify-end">
                    {kind === "teachers" && (
                      <button onClick={() => { setEditItem(item); setEditForm({ name: item.name, detail: item.detail || "", subject_ids: item.subject_ids || [], new_pin: "" }); }}
                        data-testid={`directory-edit-${item.id}`}
                        className="p-2 rounded-lg border-2 border-zinc-900 bg-[#FDE68A] hover:brightness-105 transition-colors duration-150">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => remove(item.id)} data-testid={`directory-delete-${item.id}`}
                      className="p-2 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150">
                      <Trash2 className="w-4 h-4 text-red-700" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={3} className="p-8 text-center text-zinc-500 text-sm border-t-2 border-zinc-900" data-testid="directory-empty">
                Nothing here yet — add your first {active.label.toLowerCase()} above.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {editItem && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="teacher-edit-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-bold text-xl">Edit {editItem.name}</h2>
              <button onClick={() => setEditItem(null)} data-testid="teacher-edit-close" className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={saveEdit} className="space-y-4" noValidate>
              <div>
                <label className="nb-label" htmlFor="edit-t-name">Name</label>
                <input id="edit-t-name" className="nb-input mt-2" data-testid="teacher-edit-name" value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
              </div>
              <div>
                <label className="nb-label" htmlFor="edit-t-detail">Detail</label>
                <input id="edit-t-detail" className="nb-input mt-2" data-testid="teacher-edit-detail" value={editForm.detail}
                  onChange={(e) => setEditForm({ ...editForm, detail: e.target.value })} />
              </div>
              <div>
                <span className="nb-label">Subjects they teach</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {subjects.map((s) => {
                    const on = editForm.subject_ids.includes(s.id);
                    return (
                      <button key={s.id} type="button" data-testid={`teacher-edit-subject-${s.id}`} onClick={() => toggleEditSubject(s.id)}
                        className={`px-3 py-1.5 rounded-xl border-2 border-zinc-900 text-xs font-bold transition-[transform,box-shadow,background-color] duration-150 ${on ? "bg-[#A7E4D3] shadow-[2px_2px_0px_0px_#18181B]" : "bg-white text-zinc-500"}`}>
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="nb-label flex items-center gap-1.5" htmlFor="edit-t-pin"><KeyRound className="w-3.5 h-3.5" /> Teacher login PIN (set or reset)</label>
                <input id="edit-t-pin" className="nb-input mt-2" data-testid="teacher-edit-pin" placeholder={editItem.has_pin ? "PIN is set — enter a new one to reset" : "4–8 digit PIN"}
                  value={editForm.new_pin} onChange={(e) => setEditForm({ ...editForm, new_pin: e.target.value })} minLength={4} maxLength={8} />
                <p className="text-xs text-zinc-400 font-semibold mt-1">Teachers log in with the school join code + their name + this PIN.</p>
              </div>
              <button type="submit" data-testid="teacher-edit-save" className="nb-btn w-full bg-[#1E3A8A] text-white py-2.5">Save</button>
            </form>
          </div>
        </div>
      )}

      {importOpen && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="import-modal">
          <div className="nb-card p-6 w-full max-w-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-xl flex items-center gap-2"><Upload className="w-5 h-5" /> Import {active.label}</h2>
              <button onClick={() => setImportOpen(false)} data-testid="import-close" className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-zinc-500">One entry per line: <span className="font-mono font-bold">Name, Detail (optional)</span>. Duplicates are skipped automatically.</p>
            <input type="file" accept=".csv,.txt" data-testid="import-file-input" className="mt-4 text-sm font-semibold"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) f.text().then(setImportText); }} />
            <textarea className="nb-input mt-3 min-h-[140px] resize-y font-mono text-sm" data-testid="import-textarea"
              placeholder={"Mrs. Smith, Maths\nMr. Jones, Science\nMiss Patel, English"}
              value={importText} onChange={(e) => setImportText(e.target.value)} />
            <button onClick={doImport} disabled={busy || !importText.trim()} data-testid="import-submit-btn" className="nb-btn w-full bg-[#A7E4D3] py-2.5 mt-4">
              {busy ? "Importing…" : "Import"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
